import React, { useMemo } from "react";
import "./PacingTooltip.scss";
import * as R from "ramda";
import {
  LEGEND_CUMULATIVE_ACTUAL_CLEARED,
  LEGEND_CUMULATIVE_ACTUAL_PROJECTED,
  LEGEND_CUMULATIVE_IDEAL_CLEARED,
  LEGEND_CUMULATIVE_IDEAL_PROJECTED,
  LEGEND_DAILY_ACTUAL_CLEARED,
  LEGEND_DAILY_ACTUAL_PROJECTED,
  LEGEND_DAILY_IDEAL_CLEARED,
  LEGEND_DAILY_IDEAL_PROJECTED,
} from "./ModalCustomLegendIcons";
import { getFormattedValue } from "../Components";
import { TooltipProps } from "recharts";
import { ValueType, NameType } from "recharts/types/component/DefaultTooltipContent";

const KEYS_BAR_GRAPH_PAST = ["clearedSimpleSpend", "clearedSimpleIdealSpend"];
const KEYS_BAR_GRAPH_FUTURE = ["projectedSimpleSpend", "projectedAdjustedSpend"];
const KEYS_LINE_GRAPH_PAST = ["cumulativeClearedSimpleSpend", "cumulativeClearedSimpleIdealSpend"];
const KEYS_LINE_GRAPH_FUTURE = [
  "cumulativeProjectedSimpleSpend",
  "cumulativeProjectedAdjustedSpend",
];

const PacingTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<ValueType, NameType>): JSX.Element => {
  const tooltipType = useMemo(() => {
    if (active && payload && payload.length && payload[0].dataKey) {
      if (R.includes(payload[0].dataKey, KEYS_BAR_GRAPH_PAST)) {
        return "Bar Actual";
      } else if (R.includes(payload[0].dataKey, KEYS_BAR_GRAPH_FUTURE)) {
        return "Bar Projected";
      } else if (R.includes(payload[0].dataKey, KEYS_LINE_GRAPH_PAST)) {
        return "Line Actual";
      } else if (R.includes(payload[0].dataKey, KEYS_LINE_GRAPH_FUTURE)) {
        return "Line Projected";
      }
    }
    return null;
  }, [payload, active]);

  const actualDelivery = useMemo(() => {
    switch (tooltipType) {
      case "Bar Actual":
        return "clearedSimpleSpend";
      case "Bar Projected":
        return "projectedSimpleSpend";
      case "Line Actual":
        return "cumulativeClearedSimpleSpend";
      case "Line Projected":
        return "cumulativeProjectedSimpleSpend";
      default:
        return "";
    }
  }, [tooltipType]);

  const idealDelivery = useMemo(() => {
    switch (tooltipType) {
      case "Bar Actual":
        return "clearedSimpleIdealSpend";
      case "Bar Projected":
        return "projectedAdjustedSpend";
      case "Line Actual":
        return "cumulativeClearedSimpleIdealSpend";
      case "Line Projected":
        return "cumulativeProjectedAdjustedSpend";
      default:
        return "";
    }
  }, [tooltipType]);

  const legendLabel = useMemo(() => (tooltipType ? tooltipType.split(" ")[1] : ""), [tooltipType]);

  const legendIcons = useMemo(() => {
    switch (tooltipType) {
      case "Bar Actual":
        return [LEGEND_DAILY_ACTUAL_CLEARED, LEGEND_DAILY_IDEAL_CLEARED];
      case "Bar Projected":
        return [LEGEND_DAILY_ACTUAL_PROJECTED, LEGEND_DAILY_IDEAL_PROJECTED];
      case "Line Actual":
        return [LEGEND_CUMULATIVE_ACTUAL_CLEARED, LEGEND_CUMULATIVE_IDEAL_CLEARED];
      case "Line Projected":
        return [LEGEND_CUMULATIVE_ACTUAL_PROJECTED, LEGEND_CUMULATIVE_IDEAL_PROJECTED];
      default:
        return [];
    }
  }, [tooltipType]);

  return (
    <div className="pacingTooltip" style={{ visibility: tooltipType ? "visible" : "hidden" }}>
      {tooltipType && payload && (
        <>
          <div className="tooltipDate">{R.replace(/-/g, "/", label)}</div>
          <div className="tooltipLegend">
            <span className="deliveryLabel">Actual Delivery</span>
            <div className="deliveryRow">
              <div className="legendKey">
                {legendIcons[0]}
                {legendLabel}
              </div>
              <div className="legendValue">
                {getFormattedValue({
                  value: payload[0].payload[actualDelivery],
                  type: "$",
                })}
              </div>
            </div>
            <span className="deliveryLabel">Ideal Delivery</span>
            <div className="deliveryRow">
              <div className="legendKey">
                {legendIcons[1]}
                {legendLabel}
              </div>
              <div className="legendValue">
                {getFormattedValue({
                  value: payload[0].payload[idealDelivery],
                  type: "$",
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PacingTooltip;
